import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";

export type FeatureFlag = {
  name: string;
  stringValue: string;
  boolValue: string;
  kind: string;
};

export type CustomProps = {
  key: string;
  value: string;
};

export type AllFeatbitFlagsOptions = {
  customProps?: CustomProps[];
  defaultValue?: string;
};

export type FeatbitAbFeatureOption = {
  featureFlagName: string;
  customProps: CustomProps[];
  defaultValue: string;
};

function getUserProps(user: { sessionId?: string; emailAddress?: string }) {
  return [
    { key: "SessionId", value: user.sessionId ?? "" },
    { key: "Email", value: user.emailAddress ?? "" },
  ] as CustomProps[];
}

async function getABFeatureFlag(
  requestBody: FeatbitAbFeatureOption,
  user: { sessionId?: string; emailAddress?: string },
): Promise<Throwable<FeatureFlag>> {
  try {
    const userProps = getUserProps(user);

    requestBody.customProps = requestBody.customProps
      ? requestBody.customProps.concat(userProps)
      : userProps;

    const response = await fetch("/api/getabfeatureflag/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      if (response.status >= 500) {
        throw new Error(
          `Fetch for Featbit Feature: ${requestBody.featureFlagName} from /api/getabfeatureflag failed with status ${response.status}`,
        );
      }
      return {
        isError: false,
        value: {
          name: requestBody.featureFlagName,
          stringValue: requestBody.defaultValue,
        } as FeatureFlag,
      };
    }
    const result = await response.json<FeatureFlag>();
    return { isError: false, value: result };
  } catch (error) {
    return { isError: true, error: toError(error) };
  }
}

export default getABFeatureFlag;

export async function getAllABVariationFlags(
  requestBody: AllFeatbitFlagsOptions,
  user: { sessionId?: string; emailAddress?: string },
): Promise<Throwable<FeatureFlag[]>> {
  try {
    const userProps = getUserProps(user);

    requestBody.customProps = requestBody.customProps
      ? requestBody.customProps.concat(userProps)
      : userProps;

    const response = await fetch("/api/getabfeatureflags", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      throw new Error(
        `Fetch for Featbit Feature: test from /api/getabfeatureflags failed with status ${response.status}`,
      );
    }
    const result = await response.json<FeatureFlag[]>();
    const returnValue: Throwable<FeatureFlag[]> = {
      isError: false,
      value: result,
    };
    return returnValue;
  } catch (error) {
    return { isError: true, error: toError(error) };
  }
}
